'use client';

import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Images } from "../components/Images";
import { processImages, initializeModel, getModelInfo } from "../../lib/process";
import { useAuth } from "../context/AuthContext";
import { auth } from '../firebase';
import { Loader2, Upload, CloudLightning, Zap, ArrowRight, CheckCircle2, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import { Pricing } from "./Pricing";

interface AppError {
    message: string;
}

export interface ImageFile {
    id: number;
    file: File;
    processedFile?: File;
    isHD?: boolean;
}

const sampleImages = [
    "https://images.unsplash.com/photo-1601233749202-95d04d5b3c00?q=80&w=2938&auto=format&fit=crop&ixlib=rb-4.0.3",
    "https://images.unsplash.com/photo-1513013156887-d2bf241c8c82?q=80&w=2970&auto=format&fit=crop&ixlib=rb-4.0.3",
    "https://images.unsplash.com/photo-1643490745745-e8ca9a3a1c90?q=80&w=2874&auto=format&fit=crop&ixlib=rb-4.0.3",
    "https://images.unsplash.com/photo-1574158622682-e40e69881006?q=80&w=2333&auto=format&fit=crop&ixlib=rb-4.0.3"
];

const isMobileSafari = () => {
    const ua = window.navigator.userAgent;
    const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
    const webkit = !!ua.match(/WebKit/i);
    const iOSSafari = iOS && webkit && !ua.match(/CriOS/i) && !ua.match(/OPiOS/i) && !ua.match(/FxiOS/i);
    return iOSSafari && 'ontouchend' in document;
};

export function Home() {
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<AppError | null>(null);
    const [isWebGPU, setIsWebGPU] = useState(false);
    const [images, setImages] = useState<ImageFile[]>([]);

    useEffect(() => {
        // Disabled mobile redirect to force desktop view
        /*
        if (isMobileSafari()) {
            window.location.href = 'https://bg-mobile.addy.ie';
            return;
        }
        */


        setIsLoading(false);
    }, []);



    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        // Store files
        setImages(prev => {
            const newImages = acceptedFiles.map((file, index) => ({
                id: Date.now() + index,
                file,
                processedFile: undefined,
                isHD: false
            }));
            const updated = [...prev, ...newImages];
            return updated;
        });

        // Trigger processing immediately 
        handleSelectFreeMode(acceptedFiles);
    }, []);

    const handlePaste = async (event: React.ClipboardEvent) => {
        const clipboardItems = event.clipboardData.items;
        const imageFiles: File[] = [];
        for (const item of clipboardItems) {
            if (item.type.startsWith("image")) {
                const file = item.getAsFile();
                if (file) {
                    imageFiles.push(file);
                }
            }
        }
        if (imageFiles.length > 0) {
            onDrop(imageFiles);
        }
    };

    const handleSampleImageClick = async (url: string) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const file = new File([blob], 'sample-image.jpg', { type: 'image/jpeg' });
            onDrop([file]);
        } catch (error) {
            console.error('Error loading sample image:', error);
        }
    };

    const {
        getRootProps,
        getInputProps,
        isDragActive,
        isDragAccept,
        isDragReject,
    } = useDropzone({
        onDrop,
        accept: {
            "image/*": [".jpeg", ".jpg", ".png", ".mp4"],
        },
    });

    // Refactored processing function to handle new files
    const handleSelectFreeMode = async (newFiles?: File[]) => {
        // Use provided files or fallback to all images state (though state update might be async)
        // Better to just start processing.

        setIsLoading(true);
        setError(null);
        try {
            const initialized = await initializeModel();
            if (!initialized) {
                throw new Error("Failed to initialize background removal model");
            }
            const { isWebGPUSupported } = getModelInfo();
            setIsWebGPU(isWebGPUSupported);
        } catch (err) {
            setError({
                message: err instanceof Error ? err.message : "An unknown error occurred"
            });
            setImages([]);
            setIsLoading(false);
            return;
        }
        setIsLoading(false);

        // Process images (simplified for immediate processing)
        // If newFiles are passed, we could prioritize them, but here we iterate existing 'images'
        // For simplicity with React state batching, we will rely on effect or just process current batch.
        // Actually, onDrop's setImages is async. Let's make this function robust.

        // RE-READING LOGIC: handleSelectFreeMode iterated 'images'. 
        // We'll trust the user wants to process *all* unprocessed images.
        // But since state update is async, 'images' might be empty inside onDrop call.

        // FIX: We will just accept the files to process directly if called from onDrop
        const filesToProcess = newFiles || images.map(i => i.file);

        for (const file of filesToProcess) {
            try {
                const result = await processImages([file]);
                if (result && result.length > 0) {
                    setImages(prev => prev.map(img =>
                        // Match by filename and approx timestamp if needed, or better, 
                        // we can't easily match file object equality if `processImages` takes raw file.
                        // We generated IDs in onDrop. 
                        // Let's just match by matching the file object reference if possible, or assume simple FIFO.
                        // Actually, 'images' state contains the ID.
                        img.file === file ? { ...img, processedFile: result[0] } : img
                    ));
                }
            } catch (error) {
                console.error('[v0] Error processing image:', error);
            }
        }
    };

    // ... (rest of component, removing the Control Bar div) ...

    return (
        <div className="w-full" onPaste={handlePaste}>
            {/* Non-Logged-In User Prompt */}


            {/* Adjust hero section when user is logged in */}
            {user && <div className="h-10" />}
            {/* Hero Section */}
            {images.length === 0 && (
                <>
                    <div className="flex items-center justify-center bg-gradient-to-br from-background via-background to-card px-4 pt-16 pb-12">
                        <div className="max-w-6xl w-full">
                            <div className="text-center mb-12">
                                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
                                    <Zap className="h-4 w-4 text-primary" />
                                    <span className="text-sm font-medium text-primary">Powered by Advanced AI</span>
                                </div>
                                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6 leading-tight">
                                    Remove Backgrounds
                                    <br />
                                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                                        Instantly
                                    </span>
                                </h1>
                                <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                                    Say goodbye to tedious manual editing. Our AI-powered background remover works instantly, perfectly, and completely free.
                                </p>
                            </div>

                            {/* Upload Area */}
                            <div
                                {...getRootProps()}
                                className={`
                relative overflow-hidden p-8 md:p-12 border-2 border-dashed rounded-2xl transition-all duration-300 cursor-pointer
                ${isDragAccept ? "border-primary bg-primary/5" : ""}
                ${isDragReject ? "border-red-500 bg-red-50/5" : ""}
                ${isDragActive ? "border-primary bg-primary/5 scale-[1.02]" : "border-border hover:border-primary hover:bg-primary/5 bg-card"}
                ${isLoading ? "cursor-not-allowed opacity-70" : ""}
            `}
                            >
                                <input {...getInputProps()} className="hidden" disabled={isLoading} />

                                <div className="flex flex-col items-center gap-4 z-10 relative">
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="w-12 h-12 text-primary animate-spin" />
                                            <p className="text-lg font-medium text-foreground">
                                                Loading AI Engine...
                                            </p>
                                        </>
                                    ) : error ? (
                                        <>
                                            <div className="p-3 bg-red-500/10 rounded-full">
                                                <CloudLightning className="w-8 h-8 text-red-500" />
                                            </div>
                                            <p className="text-lg font-medium text-red-500">{error.message}</p>
                                        </>
                                    ) : (
                                        <>
                                            <div className={`p-4 rounded-full transition-colors ${isDragActive ? 'bg-primary/20' : 'bg-primary/10'}`}>
                                                <Upload className={`w-8 h-8 ${isDragActive ? 'text-primary' : 'text-primary/60'}`} />
                                            </div>
                                            <div className="text-center">
                                                <p className="text-2xl font-semibold text-foreground mb-2">
                                                    {isDragActive ? "Drop your images here" : "Upload your image"}
                                                </p>
                                                <p className="text-muted-foreground">
                                                    Drag and drop or click to browse
                                                </p>
                                            </div>
                                            <div className="flex gap-2 text-xs font-medium text-muted-foreground mt-4">
                                                <span className="px-3 py-1 rounded-full bg-primary/10">JPG</span>
                                                <span className="px-3 py-1 rounded-full bg-primary/10">PNG</span>
                                                <span className="px-3 py-1 rounded-full bg-primary/10">WebP</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Features */}
                            <div className="grid md:grid-cols-3 gap-6 mt-12">
                                <div className="flex gap-4">
                                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                                    <div>
                                        <h3 className="font-semibold text-foreground mb-1">100% Client-Side</h3>
                                        <p className="text-sm text-muted-foreground">Your images never leave your device</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                                    <div>
                                        <h3 className="font-semibold text-foreground mb-1">Instant Results</h3>
                                        <p className="text-sm text-muted-foreground">Process images in seconds</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                                    <div>
                                        <h3 className="font-semibold text-foreground mb-1">Completely Free</h3>
                                        <p className="text-sm text-muted-foreground">No sign up required</p>
                                    </div>
                                </div>
                            </div>

                            {/* Sample Images */}
                            <div className="mt-12">
                                <p className="text-sm font-medium text-muted-foreground mb-4 text-center">Try with sample images:</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {sampleImages.map((url, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleSampleImageClick(url)}
                                            className="group relative aspect-square overflow-hidden rounded-xl bg-card hover:ring-2 hover:ring-primary transition-all focus:outline-none"
                                        >
                                            <img
                                                src={url || "/placeholder.svg"}
                                                alt={`Sample ${index + 1}`}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                            />
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
                        <div className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-accent/20 rounded-3xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                            <div className="relative p-8 md:p-12 rounded-3xl bg-card/50 backdrop-blur-xl border border-border overflow-hidden">
                                <div className="grid md:grid-cols-2 gap-12 items-center">
                                    <div className="space-y-6">
                                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-medium text-primary uppercase tracking-wider">
                                            Partner Product
                                        </div>
                                        <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                                            Meet Barcodely: Global Product Data <span className="text-primary italic">Simplified.</span>
                                        </h2>
                                        <p className="text-lg text-muted-foreground leading-relaxed">
                                            Barcodely is a leading provider of global product data, specializing in barcode lookup and API services. Easily integrate comprehensive product information into your applications, PoS systems, and inventory tools.
                                        </p>
                                        <div className="flex flex-wrap gap-4 pt-4">
                                            <a
                                                href="https://barcodely.cloud"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-all shadow-lg shadow-primary/20"
                                            >
                                                Explore Barcodely.cloud
                                                <ArrowRight className="h-4 w-4" />
                                            </a>
                                        </div>
                                    </div>
                                    <div className="relative">
                                        <div className="aspect-video rounded-2xl bg-white/50 backdrop-blur-sm border border-border/50 p-4 flex items-center justify-center overflow-hidden">
                                            <img
                                                src="/barcodely_graphic.png"
                                                alt="Barcodely Scanner"
                                                className="w-full h-full object-contain mix-blend-multiply hover:scale-105 transition-transform duration-700"
                                            />
                                        </div>
                                        {/* Decorative elements */}
                                        <div className="absolute -top-6 -right-6 w-32 h-32 bg-primary/10 rounded-full blur-3xl"></div>
                                        <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-accent/10 rounded-full blur-3xl"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Pricing />
                </>
            )}

            {/* Processing Area */}
            {images.length > 0 && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Images Gallery */}
                    <Images images={images} onDelete={(id) => setImages(prev => prev.filter(img => img.id !== id))} />

                    {/* Back to Upload */}
                    <div className="mt-8 text-center">
                        <button
                            onClick={() => setImages([])}
                            className="px-6 py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                        >
                            ← Back to Upload
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
